import { SelectFolderOrderDisplayModalComponentComponent } from '../../shared/modals/select-folder-order-display-modal-component/select-folder-order-display-modal-component.component';
import { PendParticleDisplayModalComponent } from '../../shared/modals/pend-particle-display-modal/pend-particle-display-modal.component';
import {
    ManageFolderDialogComponent,
    ManageFolderDialogResult,
} from '../../shared/modals/manage-folder-dialog/manage-folder-dialog.component';
import {
    addFolderToParticle,
    normalizeFolderId,
    particleInFolder,
    removeFolderFromParticle,
} from '../../workspace/folder/folder.logic';
import { deepCompare, deepCopy, parseEmbeddedTimestampsToFirebaseDate, toFirebaseTimestamp } from '@newgenus/common';
import {
    MenuConfiguration,
    CurrentConfig,
    Particle,
    Folder,
    Organization,
    OrgHat,
    OrgPolicy,
    PolicyAcknowledgment,
} from '@newgenus/common';
import { selectOutBasketParticles } from '../../redux/particles/particles.selectors';
import { selectWorkspace } from '../../redux/workspace/workspace.selectors';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { SecurityService } from '../../shared/services/security.service';
import { ParticleService } from '../../shared/services/particle.service';
import { Subject, combineLatest, debounceTime, distinctUntilChanged, map, skipWhile, take, takeUntil } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ThemeService } from '../../shared/services/theme.service';
import { MenuConfigurations } from '../../shared/utils/constants';
import { trigger, state, style } from '@angular/animations';
import { StoreUser } from '../../workspace/user.abstract';
import { displayBasketCount } from '../../workspace/basket-count';
import { NavigationEnd, Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { AppState } from '../../redux/reducers';
import { select, Store } from '@ngrx/store';
import * as moment from 'moment';
import { OrgEntityColorsService } from '@newgenus/angular-shared-components';

@Component({
    selector: 'newgenus-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    animations: [
        trigger('subNavAnimation', [
            state(
                'collapsed',
                style({
                    width: '60px',
                    // 'font-size': '0px'
                })
            ),
            state('expanded', style({ width: '250px' })),
        ]),
    ],
    standalone: false,
})
export class SidebarComponent extends StoreUser implements OnInit, OnDestroy {
    public sectionToHighlight!: string; // TODO this is used in the broken modal of custom folders. See what it's for...

    private sidebarCollapsedLocally = false;
    public isSubNavSidebarClosed = false;
    public subNavSidebarState = 'expanded';
    public isSmallDisplay = false;
    public inbasketCount!: number;
    public pendingCount!: number;
    public pendingPoliciesCount = 0;

    public customFolders: any;
    public allFolders: string[] = [];
    public basketTools: any[];

    private userMenuConfigurations!: MenuConfiguration;
    private currentMenuProfile = '';
    public configuration: CurrentConfig = {
        header: '',
        showFolders: false,
        topTierMenuItems: [],
        middleTierMenuItems: [],
    };

    private isSubscribedToUserChangesForSidebarUpdates = false;
    private allowLoopingSidebarNavigation = false;
    /**
     * Only change this variable for desktop displays.
     * @memberof SidebarComponent
     */
    public isHoveringSideBar = false;
    public expandOnHover = false;

    private destroy$ = new Subject<void>();

    constructor(
        private particleService: ParticleService,
        public security: SecurityService,
        public override store: Store<AppState>,
        private themeService: ThemeService,
        private dialog: MatDialog,
        private router: Router,
        private db: AngularFirestore,
        private auth: AngularFireAuth,
        public entityColors: OrgEntityColorsService
    ) {
        super(store);
        this.initStoreUser(this.destroy$);

        this.basketTools = this.particleService.basketTools;
    }

    public ngOnInit(): void {
        // Initially set the sidebar from the user preferences.(StoreUser abstract class)
        this.user$
            .pipe(
                skipWhile((users) => !users || users.length === 0),
                take(1)
            )
            .subscribe((users) => {
                if (users && users.length > 0 && this.isSubNavSidebarClosed !== users[0].preferences.sidebarStatus) {
                    this.toggleExpand(true);
                }
            });

        // Listens for Angular Router navigation events.
        // Reacts only to NavigationEnd events (successful navigation).
        // Updates the sidebar navigation dynamically based on the current route.
        // Prevents unnecessary updates by disabling looping navigation.
        // Subscribes to user changes to keep the sidebar in sync.
        // Cleans up the subscription when the component is destroyed to avoid memory leaks.
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd && event.url !== '/splash') {
                this.allowLoopingSidebarNavigation = false;
                this.updateSidebarNavigation(event);
                this.subscribeToUserChangesForSidebarUpdates();
            }
        });
        // A cold load (hard refresh, deep link) completes its first navigation BEFORE this
        // component exists, so the NavigationEnd above never arrives and the menu was never
        // built until the next in-app navigation — a blank sidebar on /app/issues/list until
        // List or Board was clicked (Paul, 2026-09-09). Seed from the current URL as well.
        const currentUrl = this.router.url;
        if (currentUrl && currentUrl !== '/' && currentUrl !== '/splash' && !currentUrl.startsWith('/login')) {
            this.updateSidebarNavigation();
            this.subscribeToUserChangesForSidebarUpdates();
        }

        this.particleService.userFolders$.pipe(takeUntil(this.destroy$)).subscribe((customFolders) => {
            if (customFolders?.length > 0) {
                this.customFolders = this.particleService.sortFolders(customFolders);
                // ASSIGN, never concat: userFolders$ re-emits on every Firestore
                // change, and concat grew allFolders unbounded with duplicate ids —
                // CDK re-wired every connected drop list against an ever-growing
                // array each tick, grinding the app to a halt.
                //
                // The set must be the FULL, SYMMETRIC drop-target set (baskets +
                // Filed + folders), matching the lists' connectedTo. CDK notifies
                // receiving-start from the container the item is currently IN and
                // revokes from the container it LEAVES — when this list was
                // folders-only, grants issued by the source list were never
                // revoked and re-grants never reached the basket rows, so the
                // target tints stranded/flickered mid-drag ("states confused").
                this.allFolders = [
                    'basket-1',
                    'basket-2',
                    'basket-3',
                    'basket-4',
                    ...customFolders.map((c: { id: any }) => c.id),
                ];
            } else {
                this.customFolders = [];
                this.allFolders = ['basket-1', 'basket-2', 'basket-3', 'basket-4'];
            }
        });

        // Collapse the sidebar on small displays and restore it when the display
        // grows. The small-display signal is owned by ThemeService and driven by the
        // CDK-based BreakpointService (768px parity) — this component just reacts to
        // it (no more window-resize listener here).
        this.themeService.isSmallDisplay$
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((isSmall) => {
                if (isSmall !== this.isSmallDisplay) {
                    this.isSmallDisplay = isSmall;

                    if (this.isSmallDisplay && !this.isSubNavSidebarClosed) {
                        this.toggleExpand(true);
                        this.sidebarCollapsedLocally = true;
                    } else if (!this.isSmallDisplay && this.isSubNavSidebarClosed && this.sidebarCollapsedLocally) {
                        this.toggleExpand(true);
                        this.sidebarCollapsedLocally = false;
                    }
                }
            });

        this.store
            .pipe(select(selectWorkspace))
            .pipe(takeUntil(this.destroy$))
            .subscribe((workspace) => {
                // Clamped on the way in (OE-149): a workspace poisoned before the
                // counter trigger was fixed can still hold a negative number, and
                // `|| 0` below does not catch it — `-13` is truthy.
                this.inbasketCount =
                    workspace.length > 0 ? displayBasketCount(workspace[0].inbasketCount) : this.inbasketCount;
                this.pendingCount =
                    workspace.length > 0 ? displayBasketCount(workspace[0].pendingCount) : this.pendingCount;

                if (!this.userMenuConfigurations || !this.userMenuConfigurations['WORKSPACE']) {
                    MenuConfigurations['WORKSPACE'].topTierMenuItems[0].count = this.inbasketCount || 0;
                    MenuConfigurations['WORKSPACE'].topTierMenuItems[1].count = this.pendingCount || 0;
                } else {
                    // Update inbasket count.
                    const inbasketIdx = this.userMenuConfigurations['WORKSPACE'].topTierMenuItems.findIndex(
                        (item) => item.id === 'basket-1'
                    );
                    if (~inbasketIdx) {
                        this.userMenuConfigurations['WORKSPACE'].topTierMenuItems[inbasketIdx].count =
                            this.inbasketCount || 0;
                    }

                    // Update pending count.
                    const pendingIdx = this.userMenuConfigurations['WORKSPACE'].topTierMenuItems.findIndex(
                        (item) => item.id === 'basket-2'
                    );
                    if (~pendingIdx) {
                        this.userMenuConfigurations['WORKSPACE'].topTierMenuItems[pendingIdx].count =
                            this.pendingCount || 0;
                    }
                }
                // MenuConfigurations.WORKSPACE.topTierMenuItems[2].count = doc.filedCount || 0; // TODO this is outbasket
                // MenuConfigurations.WORKSPACE.middleTierMenuItems[0].count = doc.filedCount || 0;
            });

        this.store
            .pipe(select(selectOutBasketParticles))
            .pipe(takeUntil(this.destroy$))
            .subscribe((particlesPromise) => {
                // show only last 24 hours in outbasket
                // const forLastDay = moment().subtract(1, "days");
                if (particlesPromise !== undefined) {
                    particlesPromise.then((particles) => {
                        const selectedParticles = [];
                        for (const particle of particles) {
                            if (this.todayOnly(particle)) {
                                selectedParticles.push(particle);
                            }
                        }

                        const outbasketParticleCount = selectedParticles.length;

                        if (!this.userMenuConfigurations || !this.userMenuConfigurations['WORKSPACE']) {
                            MenuConfigurations['WORKSPACE'].topTierMenuItems[2].count = outbasketParticleCount || 0;
                        } else {
                            // Update outbasket count.
                            const outbasketIdx = this.userMenuConfigurations['WORKSPACE'].topTierMenuItems.findIndex(
                                (item) => item.id === 'basket-3'
                            );
                            if (~outbasketIdx) {
                                this.userMenuConfigurations['WORKSPACE'].topTierMenuItems[outbasketIdx].count =
                                    outbasketParticleCount || 0;
                            }
                            // this.userMenuConfigurations.WORKSPACE.topTierMenuItems[2].count = this.particleCount || 0;
                        }
                    });
                }
            });

        // Subscribe to pending policies count for My Policies menu badge
        this.subscribeToPendingPoliciesCount();

        // Subscribe to hats count for My Hats menu badge
        this.subscribeToHatsCount();
    }

    /**
     * Subscribe to count pending policies that require user acknowledgment.
     * Updates the My Policies menu item badge count.
     *
     * Uses policyAcknowledgments as source of truth (not hat.policyKeys) to avoid
     * org-wide hat updates triggering task engine when policies are published.
     */
    private subscribeToPendingPoliciesCount(): void {
        this.user$
            .pipe(
                skipWhile((users) => !users || users.length === 0),
                takeUntil(this.destroy$)
            )
            .subscribe((users) => {
                if (!users || users.length === 0) return;

                const user = users[0];
                const orgKey = user.organizations?.selected;
                if (!orgKey) return;

                // Subscribe to user's acknowledgments directly (source of truth)
                this.auth.user.pipe(take(1)).subscribe((authUser) => {
                    if (!authUser) return;

                    const uid = authUser.uid;

                    // Subscribe to BOTH org document and acknowledgments for reactive updates
                    // This ensures the count updates when either acknowledgments OR policies change.
                    //
                    // Source of truth: policyAcknowledgments subcollection. The presence of an ack
                    // doc IS the proof that the policy applies to this user — we do NOT intersect
                    // with orgUsers[uid].hats or policy.targets.resolvedHatKeys, both of which can
                    // be stale denormalized caches and were observed out of sync in production.
                    combineLatest([
                        this.db.collection('organizations').doc(orgKey).valueChanges(),
                        this.db
                            .collection('organizations')
                            .doc(orgKey)
                            .collection('policyAcknowledgments', (ref) => ref.where('uid', '==', uid))
                            .valueChanges(),
                    ])
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(([orgData, acknowledgments]: [any, any[]]) => {
                            if (!orgData) return;

                            const orgPolicies = orgData.orgPolicies || {};

                            // Count unique published policies the user has an ack doc for.
                            const publishedPolicyKeys = new Set<string>();
                            for (const ack of acknowledgments) {
                                const policy = orgPolicies[ack.policyKey] as OrgPolicy | undefined;
                                if (policy?.status !== 'published') continue;
                                if (policy?.isArchived) continue;
                                publishedPolicyKeys.add(ack.policyKey);
                            }

                            const totalPoliciesCount = publishedPolicyKeys.size;
                            this.pendingPoliciesCount = totalPoliciesCount;
                            this.updateMyPoliciesMenuCount(totalPoliciesCount);
                        });
                });
            });
    }

    /**
     * Update the My Policies menu item count
     */
    private updateMyPoliciesMenuCount(count: number): void {
        // Update in MenuConfigurations
        const myPoliciesIdx = MenuConfigurations['ORGANIZATION'].topTierMenuItems.findIndex(
            (item) => item.id === 'my-policies'
        );
        if (~myPoliciesIdx) {
            MenuConfigurations['ORGANIZATION'].topTierMenuItems[myPoliciesIdx].count = count;
        }

        // Update in userMenuConfigurations if it exists
        if (this.userMenuConfigurations?.['ORGANIZATION']?.topTierMenuItems) {
            const idx = this.userMenuConfigurations['ORGANIZATION'].topTierMenuItems.findIndex(
                (item) => item.id === 'my-policies'
            );
            if (~idx) {
                this.userMenuConfigurations['ORGANIZATION'].topTierMenuItems[idx].count = count;
            }
        }
    }

    /**
     * Subscribe to count user's hats.
     * Updates the My Hats menu item badge count.
     */
    private subscribeToHatsCount(): void {
        this.user$
            .pipe(
                skipWhile((users) => !users || users.length === 0),
                takeUntil(this.destroy$)
            )
            .subscribe((users) => {
                if (!users || users.length === 0) return;

                const user = users[0];
                const orgKey = user.organizations?.selected;
                if (!orgKey) return;

                // Get organization and count user's hats.
                //
                // Source of truth: orgHats[*].assignedToArray (mirrors backend setUserHatsFrom).
                // We do NOT trust orgUsers[uid].hats — it is a denormalized cache that has been
                // observed in production with dangling refs to deleted hats AND missing live ones.
                this.db
                    .collection('organizations')
                    .doc(orgKey)
                    .valueChanges()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((org: any) => {
                        if (!org) return;

                        this.auth.user.pipe(take(1)).subscribe((authUser) => {
                            if (!authUser) return;

                            const uid = authUser.uid;
                            const orgHats = org.orgHats || {};
                            let count = 0;
                            for (const [hatKey, hat] of Object.entries(orgHats)) {
                                if (hatKey === 'hatsKeys') continue;
                                const h = hat as any;
                                if (h?.isSoftDeleted) continue;
                                if (Array.isArray(h?.assignedToArray) && h.assignedToArray.includes(uid)) {
                                    count++;
                                }
                            }
                            this.updateMyHatsMenuCount(count);
                        });
                    });
            });
    }

    /**
     * Update the My Hats menu item count
     */
    private updateMyHatsMenuCount(count: number): void {
        // Update in MenuConfigurations
        const myHatsIdx = MenuConfigurations['ORGANIZATION'].topTierMenuItems.findIndex((item) => item.id === 'hats');
        if (~myHatsIdx) {
            MenuConfigurations['ORGANIZATION'].topTierMenuItems[myHatsIdx].count = count;
        }

        // Update in userMenuConfigurations if it exists
        if (this.userMenuConfigurations?.['ORGANIZATION']?.topTierMenuItems) {
            const idx = this.userMenuConfigurations['ORGANIZATION'].topTierMenuItems.findIndex(
                (item) => item.id === 'hats'
            );
            if (~idx) {
                this.userMenuConfigurations['ORGANIZATION'].topTierMenuItems[idx].count = count;
            }
        }
    }

    public onMouseEnterSidebar(): void {
        // Only change this variable for desktop displays.
        if (!this.isSmallDisplay && this.isSubNavSidebarClosed) {
            this.isHoveringSideBar = true;
        }
    }

    public onMouseLeaveSidebar(): void {
        // Only change this variable for desktop displays.
        if (!this.isSmallDisplay) {
            this.isHoveringSideBar = false;
        }
    }

    private subscribeToUserChangesForSidebarUpdates(): void {
        if (this.isSubscribedToUserChangesForSidebarUpdates) return;

        this.isSubscribedToUserChangesForSidebarUpdates = true;
        // Short debounce only — generateSubMenuFromUserAccess is memoized and
        // resolves with a complete menu, so there is nothing to wait out here.
        // Longer delays here directly lag the sidebar highlight after navigation.
        // A module-switchboard flip re-runs it too (menus are memoised per switch version).
        combineLatest([this.user$, this.security.moduleVersion$])
            .pipe(
                map(([users]) => users),
                debounceTime(100),
                takeUntil(this.destroy$)
            )
            .subscribe(async (users) => {
                if (users && users.length > 0) {
                    const newMenu = await this.security.generateSubMenuFromUserAccess();

                    if (
                        (newMenu && !this.userMenuConfigurations) ||
                        !deepCompare(newMenu, this.userMenuConfigurations)
                    ) {
                        this.userMenuConfigurations = newMenu as MenuConfiguration;
                        this.updateSidebarNavigation();
                    }
                }
            });
    }

    public todayOnly(particle: Particle) {
        return particle.filedDate && moment(toFirebaseTimestamp(particle.filedDate).toDate()).isSame(moment(), 'day');
    }

    private updateSidebarNavigation(event?: NavigationEnd) {
        const url = event ? event.urlAfterRedirects || event.url : this.router.url;
        let moduleFragment = url.substring(url.indexOf('/', 1) + 1, url.indexOf('/', url.indexOf('/', 1) + 1));
        moduleFragment = moduleFragment.replace('/', '').replace('/', '');

        if (!this.userMenuConfigurations && moduleFragment.toUpperCase() !== 'LOGIN') {
            this.allowLoopingSidebarNavigation = true;
            setTimeout(() => {
                // Don't call this function with an old parameter if the user has navigated away (can happen if the user navigates away while the timeout is still running).
                if (this.allowLoopingSidebarNavigation) this.updateSidebarNavigation(event);
            }, 500);
        } else {
            if (moduleFragment.toUpperCase() !== 'LOGIN') {
                // if (this.currentMenuProfile != moduleFragment && moduleFragment !== 'SETTINGS') {
                // A cold load has NO current profile yet ('' until the first pick), and gating on
                // it skipped the section pick entirely: a hard refresh of /app/issues/list showed a
                // blank sidebar until the next in-app navigation (Paul, 2026-09-09). The fragment
                // alone decides; the HOME fallback below still catches unknown sections.
                if (moduleFragment && moduleFragment !== 'SETTINGS') {
                    this.currentMenuProfile = moduleFragment.toUpperCase();
                    // this.configuration = MenuConfigurations[this.currentMenuProfile];
                    this.configuration = this.userMenuConfigurations[this.currentMenuProfile];
                    this.highlightCurrentNavigation(event as NavigationEnd);
                }

                if (!this.configuration?.header) {
                    // this.configuration = MenuConfigurations['WORKSPACE'];
                    this.configuration = this.userMenuConfigurations['HOME'];
                    this.currentMenuProfile = 'HOME';
                    this.highlightCurrentNavigation(event as NavigationEnd);

                    // This is merely a workaround to adjust the margin of the inner menu container when the sidebar has loaded up.
                    this.particleService.sideBarInstantiated$.next(true);
                }
            } else {
                // Reset sidebar menu
                this.configuration = {
                    header: '',
                    showFolders: false,
                    topTierMenuItems: [],
                    middleTierMenuItems: [],
                };
            }
        }
    }

    public highlightNav(el: any): void {
        // De-highlight all other menu items.
        this.configuration?.topTierMenuItems?.forEach((c) => {
            c.isHighlighted = false;
        });
        this.configuration?.middleTierMenuItems?.forEach((c) => {
            c.isHighlighted = false;
        });
        this.customFolders?.forEach((c: { isHighlighted: boolean }) => {
            c.isHighlighted = false;
        });

        // Highlight the current menu item.
        el.isHighlighted = true;
    }

    private highlightCurrentNavigation(event: NavigationEnd): void {
        if (!this.configuration) return;

        try {
            const url = event ? event.urlAfterRedirects || event.url : this.router.url;
            const currentNavPath = url.substring(url.lastIndexOf('/') + 1)?.toLowerCase();

            // Match against item.path (the URL segment) rather than item.name (the display label).
            // e.g. URL "/app/organization/designer" → currentNavPath = "designer" → matches item with path: 'designer' (displayed as "Org Board").
            const topTierMatch = this.configuration.topTierMenuItems.findIndex(
                (x) => x.path?.toLowerCase() === currentNavPath
            );
            if (topTierMatch > -1) this.highlightNav(this.configuration.topTierMenuItems[topTierMatch]);
            else {
                const middleTierMatch = this.configuration.middleTierMenuItems.findIndex(
                    (x) => x.path?.toLowerCase() === currentNavPath
                );
                if (middleTierMatch > -1) this.highlightNav(this.configuration.middleTierMenuItems[middleTierMatch]);
                else {
                    // Custom folders route by their stable id (not the mutable name).
                    const customFolderMatch = this.customFolders?.findIndex(
                        (x: { id: string }) => x.id?.toLowerCase() === currentNavPath
                    );
                    if (customFolderMatch > -1) this.highlightNav(this.customFolders[customFolderMatch]);
                }
            }
        } catch (error) {
            console.warn('highlightCurrentNavigation > error:', error);
        }
    }

    private existingFolderNames(): string[] {
        return (this.customFolders || []).map((f: { name: string }) => f.name);
    }

    public deleteFolder(folderToDelete: Folder) {
        this.dialog
            .open(ManageFolderDialogComponent, {
                data: { action: 'delete', folder: folderToDelete, existingNames: this.existingFolderNames() },
            })
            .afterClosed()
            .subscribe((result: ManageFolderDialogResult) => {
                if (result?.action !== 'delete') return;
                this.particleService.deleteFolder(folderToDelete);
                this.cleanupParticlesForDeletedFolder(folderToDelete.id);
            });
    }

    /**
     * Strip the deleted folder's id from every particle that referenced it, so
     * no dangling membership survives (which would otherwise "resurrect" if a
     * folder with the same id were recreated).
     */
    private cleanupParticlesForDeletedFolder(folderId: string): void {
        this.particleService.userParticles$
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((particles: Particle[]) => {
                (particles || []).forEach((particle) => {
                    if (!particleInFolder(particle, folderId)) return;
                    const folders = removeFolderFromParticle(particle.folders, folderId);
                    this.particleService.updateParticleFolderViaStore({ ...deepCopy(particle), folders } as Particle);
                });
            });
    }

    public addNewFolder() {
        this.dialog
            .open(ManageFolderDialogComponent, { data: { action: 'add', existingNames: this.existingFolderNames() } })
            .afterClosed()
            .subscribe((result: ManageFolderDialogResult) => {
                if (result?.action !== 'add') return;
                const name = result.name;
                this.particleService.createFolder({
                    name,
                    count: 0,
                    icon: 'far fa-check-circle',
                    particles: [],
                    uid: this.userId,
                    section: 'folder',
                    id: normalizeFolderId(name),
                    position: this.customFolders?.length || 0,
                });
            });
    }

    public updateFolder(folderToUpdate: Folder) {
        this.dialog
            .open(ManageFolderDialogComponent, {
                data: { action: 'rename', folder: folderToUpdate, existingNames: this.existingFolderNames() },
            })
            .afterClosed()
            .subscribe((result: ManageFolderDialogResult) => {
                if (result?.action === 'rename') this.particleService.renameFolder(folderToUpdate, result.name);
            });
    }

    public openSelectFolderOrderModal(foldersToSort: Folder[]): void {
        const dialogRef = this.dialog.open(SelectFolderOrderDisplayModalComponentComponent, {
            data: foldersToSort,
        });

        dialogRef.afterClosed().subscribe((result) => {
            // Do nothing...
        });
    }

    public drop(event: CdkDragDrop<string[]>, destinationFolder: any): void {
        // Only act when the user released ON this target. CDK keeps the item in
        // the last-entered container, so a release over a non-target (Search, a
        // row gap, the source basket) still fires `dropped` on that stale
        // container — acting on it would file/pend a mail with NO visual cue.
        if (!event.isPointerOverContainer) return;

        const particle: any = event.previousContainer.data ? event.previousContainer.data[event.previousIndex] : null;
        if (event.container.id === 'basket-1') {
            // Inbasket
            this.particleService.moveParticleToInbasketViaStore(particle, this.inbasketCount, this.pendingCount);
        }

        if (event.container.id === 'basket-2') {
            // Pending
            this.openPendParticleModal(particle);
        }

        if (event.container.id === 'basket-3') {
            // Outbasket
            this.particleService.fileParticleViaStore(particle);
        }

        if (event.container.id === 'basket-4') {
            // Filed — drag-to-file
            this.particleService.fileParticleViaStore(particle);
        }

        if (event.previousContainer.id === 'folder-1') {
            moveItemInArray(event.previousContainer.data, event.previousIndex, event.currentIndex);
        } else if (destinationFolder.section?.includes('folder')) {
            // Add a virtual copy keyed by the STABLE folder id (folder.id).
            const next = addFolderToParticle(particle.folders, destinationFolder.id, destinationFolder.name);
            if (next !== particle.folders && Object.keys(next).length !== Object.keys(particle.folders || {}).length) {
                particle.folders = next;
                this.particleService.updateParticleFolderViaStore(particle);
            }
        }
    }

    /** trackBy for the folder rows — identity by stable folder id, so
     * userFolders$ re-emissions don't re-create the rows (and their
     * CdkDropLists) mid-drag. */
    public trackFolderById(_index: number, folder: Folder): string {
        return folder.id;
    }

    // The strong "you're about to drop here" highlight (.drag-over) is owned
    // by DragHighlightService: driven by the drag's real pointer coordinates
    // via (cdkDragMoved) on the list rows + elementFromPoint, applied only to
    // the row that currently HOLDS the dragged item. Neither CSS :hover
    // (Chrome doesn't update it while a mouse button is held) nor CDK
    // entered/exited toggling (no exited fires when crossing a non-target,
    // so it sticks) survived real-world usage — see the service docs.

    /**
     * Reorder the custom folders in the sidebar (drag by their handle) and
     * persist each folder's display position.
     */
    public reorderFolders(event: CdkDragDrop<any[]>): void {
        if (event.previousIndex === event.currentIndex) return;
        moveItemInArray(this.customFolders, event.previousIndex, event.currentIndex);
        this.customFolders.forEach((folder: Folder, index: number) => {
            if (folder.position !== index) {
                folder.position = index;
                this.particleService.updateCustomFolderDisplayPosition(folder);
            }
        });
    }

    private openPendParticleModal(particle: any): void {
        const dialogRef = this.dialog.open(PendParticleDisplayModalComponent, {
            data: particle,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // this.onPendParticle(result);
                this.particleService.pendParticleViaStore(result); // this.inbasketCount, this.pendingCount
            }
        });
    }

    public browseTo(section: string, folderName: string) {
        if (folderName !== 'folder') {
            const path = `app/${section}` + (folderName ? `/${folderName.toLowerCase()}` : '');
            this.router.navigate([path]);
        }
    }

    /** Get entity color for menu icon (My Hats → hats, My Policies → policies, Org Board → positions) */
    public getMenuIconColor(menuId: string): string | null {
        switch (menuId) {
            case 'hats':
                return this.entityColors.getColor('hats');
            case 'my-policies':
                return this.entityColors.getColor('policies');
            case 'designer':
                return this.entityColors.getColor('positions');
            default:
                return null;
        }
    }

    /** Get entity color for menu badge background */
    public getMenuBadgeColor(menuId: string): string | null {
        switch (menuId) {
            case 'hats':
                return this.entityColors.getColor('hats');
            case 'my-policies':
                return this.entityColors.getColor('policies');
            case 'designer':
                return this.entityColors.getColor('positions');
            default:
                return null;
        }
    }

    // public onPendParticle = (args: any) => {
    //   this.particleService.pendParticleViaStore(result); // this.inbasketCount, this.pendingCount
    // }

    public onRightClick(event: MouseEvent, menuRef: MatMenuTrigger): void {
        if (menuRef) menuRef.toggleMenu();

        event.preventDefault();
        event.stopPropagation();
    }

    public toggleExpand(shouldSkipUpdatePreference = false): void {
        const newIsSubNavClosed = !this.isSubNavSidebarClosed;
        const newSubNavState = newIsSubNavClosed ? 'collapsed' : 'expanded';

        this.themeService.localUpdateOfSidebarStatus(newIsSubNavClosed);
        if (!shouldSkipUpdatePreference) {
            const preferenceCopy = parseEmbeddedTimestampsToFirebaseDate(deepCopy(this.user.preferences));
            preferenceCopy.sidebarStatus = newIsSubNavClosed;

            this.updateUserStoreDoc({ preferences: preferenceCopy });
        }

        if (newSubNavState === 'expanded') {
            const children_list = document.getElementsByTagName('body')[0].getElementsByClassName('child-list');
            this.resetAllChildElements(children_list);
        }

        setTimeout(() => {
            this.isSubNavSidebarClosed = newIsSubNavClosed;
            this.subNavSidebarState = newSubNavState;
        });

        setTimeout(() => {
            this.expandOnHover = this.isSubNavSidebarClosed;
        }, 250);
    }

    private resetAllChildElements(children: any) {
        if (children) {
            for (let i = 0; i < children.length; i++) {
                children[i].style.display = 'none';
            }
        }
    }

    // -- DOM Functions Ends -- //

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }
}
